import { useState, useEffect } from "react";
import { Check, ChevronDown, Building, Globe, Users, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

// Types for the role data structure
interface Role {
  id: number;
  name: string;
}

interface PlatformRole extends Role {}

interface ManagementCompanyRole extends Role {
  managementCompanyId: number;
  managementCompanyName: string;
}

interface AssociationRole extends Role {
  associationId: number;
  associationName: string;
}

interface UserRoles {
  platform: PlatformRole[];
  managementCompanies: ManagementCompanyRole[];
  associations: AssociationRole[];
}

// Dummy data for now
const DUMMY_USER_ROLES: UserRoles = {
  platform: [
    { id: 1, name: "Super Admin" },
    { id: 2, name: "Platform Manager" },
  ],
  managementCompanies: [
    { 
      id: 3, 
      name: "Admin", 
      managementCompanyId: 1, 
      managementCompanyName: "Acme Property Management" 
    },
    { 
      id: 4, 
      name: "Document Manager", 
      managementCompanyId: 1, 
      managementCompanyName: "Acme Property Management" 
    },
    { 
      id: 5, 
      name: "Admin", 
      managementCompanyId: 2, 
      managementCompanyName: "Sunset Properties LLC" 
    },
  ],
  associations: [
    {
      id: 6,
      name: "Board Member",
      associationId: 101,
      associationName: "Oceanview Condominiums"
    },
    {
      id: 7,
      name: "Property Manager",
      associationId: 101,
      associationName: "Oceanview Condominiums"
    },
    {
      id: 8,
      name: "Admin",
      associationId: 102,
      associationName: "Sunset Hills HOA"
    },
  ],
};

interface SelectedRole {
  type: 'platform' | 'management_company' | 'association';
  roleId?: number; // Optional since we're showing entities, not specific roles
  roleName?: string; // Optional since we're showing entities, not specific roles
  entityId?: number;
  entityName?: string;
}

// Utility function to format role names with title case
const formatRoleName = (name: string): string => {
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Utility function to truncate text with ellipsis
const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export function RoleSwitcher() {
  const { userRoles, loading, permissionsLoaded, selectedRole, setSelectedRole } = useAuth();

  const handleRoleSelect = (type: 'platform' | 'management_company' | 'association', entityId?: number, entityName?: string) => {
    setSelectedRole({
      type,
      entityId,
      entityName
    });
  };

  const getDisplayText = () => {
    if (!selectedRole) return 'No Role Selected';
    
    switch (selectedRole.type) {
      case 'platform':
        return 'Platform Role';
      case 'management_company':
        return 'Management Company';
      case 'association':
        return 'Association';
      default:
        return 'Role';
    }
  };

  // Calculate total number of available roles
  const getTotalRoleCount = () => {
    let count = 0;
    if (userRoles.platform.length > 0) count++;
    count += Object.keys(userRoles.managementCompanies).length;
    count += Object.keys(userRoles.associations).length;
    return count;
  };

  // Show loading if auth is loading or permissions are not loaded yet
  if (loading || !permissionsLoaded) {
    return (
      <div className="p-3 w-full flex items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="ml-2 text-xs text-gray-500">Loading permissions...</span>
      </div>
    );
  }

  if (!selectedRole && !loading) {
    return (
      <div className="p-3 w-full text-center text-sm text-gray-500">
        No roles available
      </div>
    );
  }

  const totalRoles = getTotalRoleCount();

  // If there's only one role available, show a simple display without dropdown
  if (totalRoles === 1) {
    return (
      <div className="p-3 w-full">
        <div className="flex items-start flex-col text-left min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
            {selectedRole.type === 'platform' ? (
              <Globe className="h-3 w-3 flex-shrink-0" />
            ) : selectedRole.type === 'management_company' ? (
              <Building className="h-3 w-3 flex-shrink-0" />
            ) : (
              <Users className="h-3 w-3 flex-shrink-0" />
            )}
            <span className="truncate">{getDisplayText()}</span>
          </div>
          <div className="text-sm font-medium text-gray-900 truncate w-full" title={selectedRole.entityName}>
            {selectedRole.entityName}
          </div>
        </div>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-auto p-3 w-full justify-between hover:bg-brand-orange/10 hover:text-brand-orange"
        >
          <div className="flex items-start flex-col text-left min-w-0 flex-1">
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
              {selectedRole.type === 'platform' ? (
                <Globe className="h-3 w-3 flex-shrink-0" />
              ) : selectedRole.type === 'management_company' ? (
                <Building className="h-3 w-3 flex-shrink-0" />
              ) : (
                <Users className="h-3 w-3 flex-shrink-0" />
              )}
              <span className="truncate">{getDisplayText()}</span>
            </div>
            <div className="text-sm font-medium text-gray-900 truncate w-full" title={selectedRole.entityName}>
              {selectedRole.entityName}
            </div>
          </div>
          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 max-h-[400px] overflow-y-auto">
        {/* Platform Role */}
        {userRoles.platform.length > 0 && (
          <>
            <DropdownMenuLabel className="flex items-center gap-2 text-sm font-semibold text-gray-700 py-2">
              <Globe className="h-4 w-4" />
              Platform
            </DropdownMenuLabel>
            <DropdownMenuItem
              className={cn(
                "flex items-center justify-between cursor-pointer pl-8 py-2",
                selectedRole.type === 'platform' && "bg-gray-100"
              )}
              onClick={() => handleRoleSelect('platform', undefined, 'Platform')}
            >
              <span className="text-sm text-gray-600 truncate">
                Platform Role
              </span>
              {selectedRole.type === 'platform' && (
                <Check className="h-4 w-4 text-brand-orange flex-shrink-0 ml-2" />
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Management Companies */}
        {Object.keys(userRoles.managementCompanies).length > 0 && (
          <>
            <DropdownMenuLabel className="flex items-center gap-2 text-sm font-semibold text-gray-700 py-2">
              <Building className="h-4 w-4" />
              Management Companies
            </DropdownMenuLabel>
            {Object.entries(userRoles.managementCompanies).map(([companyId, roles]) => {
              const companyName = roles[0]?.companyName || `Company ${companyId}`;
              return (
                <DropdownMenuItem
                  key={companyId}
                  className={cn(
                    "flex items-center justify-between cursor-pointer pl-8 py-2",
                    selectedRole.type === 'management_company' && 
                    selectedRole.entityId === parseInt(companyId) && "bg-gray-100"
                  )}
                  onClick={() => handleRoleSelect('management_company', parseInt(companyId), companyName)}
                >
                  <span className="text-sm text-gray-600 truncate" title={companyName}>
                    {truncateText(companyName, 30)}
                  </span>
                  {selectedRole.type === 'management_company' && 
                   selectedRole.entityId === parseInt(companyId) && (
                    <Check className="h-4 w-4 text-brand-orange flex-shrink-0 ml-2" />
                  )}
                </DropdownMenuItem>
              );
            })}
            {Object.keys(userRoles.associations).length > 0 && <DropdownMenuSeparator />}
          </>
        )}

        {/* Associations */}
        {Object.keys(userRoles.associations).length > 0 && (
          <>
            <DropdownMenuLabel className="flex items-center gap-2 text-sm font-semibold text-gray-700 py-2">
              <Users className="h-4 w-4" />
              Associations
            </DropdownMenuLabel>
            {Object.entries(userRoles.associations).map(([associationId, roles]) => {
              const associationName = roles[0]?.associationName || `Association ${associationId}`;
              return (
                <DropdownMenuItem
                  key={associationId}
                  className={cn(
                    "flex items-center justify-between cursor-pointer pl-8 py-2",
                    selectedRole.type === 'association' && 
                    selectedRole.entityId === parseInt(associationId) && "bg-gray-100"
                  )}
                  onClick={() => handleRoleSelect('association', parseInt(associationId), associationName)}
                >
                  <span className="text-sm text-gray-600 truncate" title={associationName}>
                    {truncateText(associationName, 30)}
                  </span>
                  {selectedRole.type === 'association' && 
                   selectedRole.entityId === parseInt(associationId) && (
                    <Check className="h-4 w-4 text-brand-orange flex-shrink-0 ml-2" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
} 